import React, { useState, useEffect, useMemo } from 'react';
import { Calendar as CalendarIcon, Plus, X, Clock, ShieldAlert, Coffee, MessageSquare, ChevronLeft, ChevronRight, Info, CheckSquare, Trash2, Users } from 'lucide-react';
import { Spinner, UserAvatar, RenderIcon, renderMarkdown, fmtDate, Badge, ACCENT_LIGHT } from './App';

const Field = ({ label, children, T }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>{label}</div>
    {children}
  </div>
);

export default function CalendarTab({ ctx }) {
  const { api, T, currentUser, isEN, notify, users, projects, allTasks, isAdmin } = ctx;

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  
  const [currentDate, setCurrentDate] = useState(new Date());

  const [showModal, setShowModal] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null); // 🚀 NEU: Detail-Ansicht für Termine
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: "", type: "meeting", start_time: "", end_time: "", team_id: "", description: "", participants: []
  });

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [evRes, tRes] = await Promise.all([
        api('/calendar/events').catch(() => []),
        api('/workspace/teams').catch(() => ({teams: []}))
      ]);
      setEvents(evRes || []);
      setTeams(tRes.teams || []);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const handleUpdate = () => loadData(true);
    window.addEventListener('calendar_updated', handleUpdate);
    return () => window.removeEventListener('calendar_updated', handleUpdate);
  }, []);

  const calendarItems = useMemo(() => {
    let combined = [];
    events.forEach(e => {
      let bgColor = "#3B82F6"; 
      if (e.type === 'blocker') bgColor = "#EF4444";
      if (e.type === 'absence') bgColor = "#F59E0B";
      combined.push({ id: `ev_${e.id}`, title: e.title, date: new Date(e.start_time), color: bgColor, isTask: false, raw: e });
    });
    if (allTasks && allTasks.length > 0) {
      allTasks.forEach(t => {
        const proj = projects.find(p => String(p.id) === String(t.project_id || t.projectId));
        const finalStatus = proj?.customStatuses?.length ? proj.customStatuses[proj.customStatuses.length - 1].label : 'Erledigt';
        if (t.due_date && !t.archived && t.status !== finalStatus) {
          const color = proj ? proj.color : "#8B5CF6";
          combined.push({ id: `task_${t.id}`, title: t.title, date: new Date(t.due_date), color: color, isTask: true, raw: t });
        }
      });
    }
    return combined;
  }, [events, allTasks, projects]);

  const saveEvent = async () => {
    if (!eventForm.title.trim()) return notify("Titel erforderlich!");
    if (!eventForm.start_time || !eventForm.end_time) return notify("Zeiten erforderlich!");
    setIsSubmitting(true);
    try {
      await api('/calendar/events', {
        method: 'POST',
        body: JSON.stringify({
          ...eventForm,
          start_time: new Date(eventForm.start_time).toISOString(),
          end_time: new Date(eventForm.end_time).toISOString()
        })
      });
      notify("Termin erfolgreich erstellt! 📅");
      setShowModal(false);
      loadData(true);
    } catch (e) { notify("Fehler: " + e.message); } 
    finally { setIsSubmitting(false); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Termin wirklich löschen?")) return;
    try {
      await api(`/calendar/events/${id}`, { method: "DELETE" });
      notify("Termin gelöscht!");
      setSelectedEvent(null);
      loadData(true);
    } catch(e) { notify("Fehler: " + e.message); }
  };

  const toggleParticipant = (userId) => {
    setEventForm(prev => ({
      ...prev,
      participants: prev.participants.includes(userId) 
        ? prev.participants.filter(id => id !== userId)
        : [...prev.participants, userId]
    }));
  };

  // 🚀 SMART SELECT: Team auswählen lädt automatisch alle Mitglieder ein!
  const handleTeamSelect = (e) => {
    const tId = e.target.value;
    const team = teams.find(t => String(t.id) === String(tId));
    
    setEventForm(prev => {
      // Wenn kein Team gewählt wurde, Teilnehmer so lassen
      if (!team) return { ...prev, team_id: "" };
      
      // Alle aktuellen Teilnehmer nehmen und die neuen vom Team hinzufügen (ohne Duplikate)
      const teamMemberIds = (team.members || []).map(m => m.user_id).filter(id => id !== currentUser.id);
      const newParticipants = [...new Set([...prev.participants, ...teamMemberIds])];
      
      return { ...prev, team_id: tId, participants: newParticipants };
    });
  };

  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; 

  const calendarCells = [];
  for (let i = 0; i < startOffset; i++) calendarCells.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarCells.push(i);

  if (loading) return <div style={{ padding: 60, textAlign: "center" }}><Spinner /></div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 100px)", animation: "fadeUp 0.3s ease", width: "100%" }}>
      
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <CalendarIcon size={24} color="#0ECFD9" />
            {isEN ? "Workspace Calendar" : "Workspace Kalender"}
          </h2>
          <div style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>
            {isEN ? "Meetings, blockers and task deadlines." : "Besprechungen, Ressourcen-Blocker und Deadlines."}
          </div>
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ display: "flex", background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
            <button onClick={prevMonth} className="hov" style={{ background: "transparent", border: "none", color: T.text, padding: "8px 12px", cursor: "pointer", borderRight: `1px solid ${T.border}` }}><ChevronLeft size={18}/></button>
            <button onClick={goToday} className="hov" style={{ background: "transparent", border: "none", color: T.text, padding: "8px 16px", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>Heute</button>
            <button onClick={nextMonth} className="hov" style={{ background: "transparent", border: "none", color: T.text, padding: "8px 12px", cursor: "pointer", borderLeft: `1px solid ${T.border}` }}><ChevronRight size={18}/></button>
          </div>
          
          <button onClick={() => setShowLegend(true)} className="hov" style={{ background: T.cardHover, border: `1px solid ${T.border2}`, color: T.textMuted, padding: "10px", borderRadius: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} title="Legende anzeigen">
            <Info size={18} />
          </button>

          <button onClick={() => {
              const now = new Date();
              const later = new Date(now.getTime() + 60*60*1000);
              setEventForm({ title: "", type: "meeting", start_time: now.toISOString().slice(0,16), end_time: later.toISOString().slice(0,16), team_id: "", description: "", participants: [] });
              setShowModal(true);
            }} 
            className="hov" 
            style={{ display: "flex", alignItems: "center", gap: 8, background: "#0ECFD9", color: "#000", border: "none", padding: "10px 20px", borderRadius: 12, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 12px rgba(14,207,217,0.3)" }}>
            <Plus size={18} /> {isEN ? "New Event" : "Neuer Termin"}
          </button>
        </div>
      </div>

      {/* KALENDER GRID */}
      <div style={{ flex: 1, background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "20px 24px", background: T.cardHover, borderBottom: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>
            {currentDate.toLocaleString(isEN ? 'en-US' : 'de-DE', { month: 'long', year: 'numeric' })}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, textAlign: "center" }}>
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => <div key={d} style={{ fontSize: 12, fontWeight: 800, color: T.textMuted, letterSpacing: "0.1em" }}>{d}</div>)}
          </div>
        </div>

        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridAutoRows: "minmax(100px, 1fr)" }}>
          {calendarCells.map((day, idx) => {
            if (!day) return <div key={`empty-${idx}`} style={{ background: T.bg, borderRight: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }} />;
            const cellDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const isToday = new Date().toDateString() === cellDate.toDateString();
            const dayEvents = calendarItems.filter(ci => ci.date.getFullYear() === cellDate.getFullYear() && ci.date.getMonth() === cellDate.getMonth() && ci.date.getDate() === cellDate.getDate());

            return (
              <div key={day} onClick={() => {
                  const clickDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day, 12, 0);
                  const later = new Date(clickDate.getTime() + 60*60*1000);
                  setEventForm({ title: "", type: "meeting", start_time: clickDate.toISOString().slice(0,16), end_time: later.toISOString().slice(0,16), team_id: "", description: "", participants: [] });
                  setShowModal(true);
                }}
                className="hov"
                style={{ 
                  borderRight: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, padding: 8, cursor: "pointer",
                  background: isToday ? "rgba(14,207,217,0.05)" : "transparent", display: "flex", flexDirection: "column", gap: 6, transition: "background 0.2s" 
                }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: isToday ? "#0ECFD9" : T.textSub, textAlign: "right", paddingRight: 4 }}>{day}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, overflowY: "auto", paddingRight: 4 }}>
                  {dayEvents.map(ev => (
                    <div key={ev.id} onClick={(e) => {
                        e.stopPropagation();
                        if (ev.isTask) {
                          if (ctx.setDetailTask) ctx.setDetailTask(ev.raw);
                        } else {
                          setSelectedEvent(ev.raw); // 🚀 Öffnet das neue Event Detail Dashboard
                        }
                      }}
                      title={ev.title}
                      style={{ 
                        fontSize: 11, fontWeight: 700, padding: "4px 6px", borderRadius: 6, cursor: "pointer",
                        background: ev.isTask ? `${ev.color}15` : ev.color, color: ev.isTask ? ev.color : "#fff",
                        border: ev.isTask ? `1px solid ${ev.color}40` : "none",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        display: "flex", alignItems: "center", gap: 4
                      }}
                    >
                      {ev.isTask && <CheckSquare size={10} style={{flexShrink: 0}}/>}
                      {ev.title}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 🚀 NEU: EVENT DETAIL DASHBOARD */}
      {selectedEvent && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 20 }}>
          <div style={{ background: T.sidebar, border: `1px solid ${T.border3}`, borderRadius: 22, padding: "32px", width: "100%", maxWidth: 550, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 32px 72px rgba(0,0,0,0.6)", animation: "popIn 0.3s ease" }}>
            
            {/* EVENT HEADER */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${T.border}` }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  {selectedEvent.type === 'meeting' && <Badge label="Besprechung" color="#3B82F6" bg="#3B82F615" />}
                  {selectedEvent.type === 'blocker' && <Badge label="Ressourcen Blocker" color="#EF4444" bg="#EF444415" />}
                  {selectedEvent.type === 'absence' && <Badge label="Abwesenheit" color="#F59E0B" bg="#F59E0B15" />}
                </div>
                <h3 style={{ margin: 0, color: T.text, fontSize: 22, fontWeight: 800 }}>{selectedEvent.title}</h3>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.textMuted, marginTop: 8 }}>
                  <Clock size={14} /> 
                  {fmtDate(selectedEvent.start_time)} · {new Date(selectedEvent.start_time).toLocaleTimeString(isEN ? 'en-US' : 'de-DE', {hour: '2-digit', minute:'2-digit'})} 
                  {" - "}
                  {new Date(selectedEvent.end_time).toLocaleTimeString(isEN ? 'en-US' : 'de-DE', {hour: '2-digit', minute:'2-digit'})}
                </div>
              </div>
              <button onClick={() => setSelectedEvent(null)} style={{ background: "transparent", border: "none", color: T.textMuted, cursor: "pointer" }}><X size={24}/></button>
            </div>

            {/* EVENT BODY */}
            <div className="custom-scrollbar" style={{ flex: 1, overflowY: "auto", paddingRight: 8, display: "flex", flexDirection: "column", gap: 20 }}>
              
              {(selectedEvent.team_id || (selectedEvent.participants && selectedEvent.participants.length > 0)) && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.textGhost, letterSpacing: "0.1em", marginBottom: 12 }}>{isEN ? "PARTICIPANTS & TEAM" : "TEILNEHMER & TEAM"}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                    {selectedEvent.team_name && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, background: T.cardHover, border: `1px solid ${T.border2}`, padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, color: T.text }}>
                        <Users size={14} color={ACCENT_LIGHT} /> Team {selectedEvent.team_name}
                      </div>
                    )}
                    {(selectedEvent.participants || []).map(p => (
                      <div key={p.user_id} title={p.name} style={{display: "flex", alignItems: "center", gap: 6, background: T.bg, padding: "4px 10px", borderRadius: 20, border: `1px solid ${T.border2}`}}>
                        <UserAvatar user={{id: p.user_id, name: p.name, avatar_url: p.avatar_url}} size={20} />
                        <span style={{fontSize: 12, fontWeight: 600, color: T.textSub}}>{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedEvent.description && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.textGhost, letterSpacing: "0.1em", marginBottom: 8 }}>AGENDA & NOTIZEN</div>
                  <div dangerouslySetInnerHTML={renderMarkdown(selectedEvent.description)} style={{ background: T.cardHover, padding: 16, borderRadius: 12, border: `1px solid ${T.border2}`, fontSize: 14, color: T.textSub, lineHeight: 1.6 }} />
                </div>
              )}
              
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.textFaint, marginTop: "auto" }}>
                Erstellt von <b style={{color: T.textMuted}}>{selectedEvent.creator_name || "System"}</b>
              </div>
            </div>

            {/* EVENT FOOTER (Löschen) */}
            {(isAdmin || selectedEvent.created_by === currentUser.id) && (
              <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between" }}>
                <button onClick={() => deleteEvent(selectedEvent.id)} className="hov" style={{ background: "rgba(239, 68, 68, 0.1)", border: "none", color: "#EF4444", padding: "10px 16px", borderRadius: 10, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                  <Trash2 size={16} /> Termin absagen & löschen
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* LEGENDE */}
      {showLegend && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 20 }}>
          <div style={{ background: T.sidebar, border: `1px solid ${T.border3}`, borderRadius: 22, padding: "32px", width: "100%", maxWidth: 450, display: "flex", flexDirection: "column", boxShadow: "0 32px 72px rgba(0,0,0,0.6)", animation: "popIn 0.3s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h3 style={{ margin: 0, color: T.text, fontSize: 18, fontWeight: 800, display: "flex", alignItems: "center", gap: 10 }}><Info color="#0ECFD9" /> Kalender Legende</h3>
              <button onClick={() => setShowLegend(false)} style={{ background: "transparent", border: "none", color: T.textMuted, cursor: "pointer" }}><X size={20}/></button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, background: T.cardHover, padding: "12px 16px", borderRadius: 12, border: `1px solid ${T.border2}` }}>
                <div style={{ width: 14, height: 14, borderRadius: 4, background: "#3B82F6" }} />
                <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Besprechungen & Meetings</div><div style={{ fontSize: 12, color: T.textMuted }}>Klassische Termine (Blau)</div></div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, background: T.cardHover, padding: "12px 16px", borderRadius: 12, border: `1px solid ${T.border2}` }}>
                <div style={{ width: 14, height: 14, borderRadius: 4, background: "#EF4444" }} />
                <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Ressourcen & Blocker</div><div style={{ fontSize: 12, color: T.textMuted }}>Exklusive Reservierungen wie NL2 Server (Rot)</div></div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, background: T.cardHover, padding: "12px 16px", borderRadius: 12, border: `1px solid ${T.border2}` }}>
                <div style={{ width: 14, height: 14, borderRadius: 4, background: "#F59E0B" }} />
                <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Abwesenheiten</div><div style={{ fontSize: 12, color: T.textMuted }}>Urlaub, Krankheit oder Berufsschule (Orange)</div></div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, background: T.cardHover, padding: "12px 16px", borderRadius: 12, border: `1px dashed ${T.border2}`, marginTop: 8 }}>
                <div style={{ width: 14, height: 14, borderRadius: 4, background: "rgba(139, 92, 246, 0.15)", border: "1px solid rgba(139, 92, 246, 0.4)" }} />
                <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Aufgaben Deadlines</div><div style={{ fontSize: 12, color: T.textMuted }}>Werden vollautomatisch generiert und übernehmen die Farbe deines jeweiligen Projekts. Ein Klick öffnet die Aufgabe!</div></div>
              </div>
            </div>
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${T.border}`, textAlign: "center" }}>
              <button onClick={() => setShowLegend(false)} className="hov" style={{ background: "#0ECFD9", border: "none", color: "#000", padding: "10px 24px", borderRadius: 10, fontWeight: 800, cursor: "pointer", width: "100%" }}>Verstanden</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: NEUER TERMIN */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 20 }}>
          <div style={{ background: T.sidebar, border: `1px solid ${T.border3}`, borderRadius: 22, padding: "32px", width: "100%", maxWidth: 550, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 32px 72px rgba(0,0,0,0.6)", animation: "popIn 0.3s ease" }}>
            
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: T.text, fontSize: 18, fontWeight: 800, display: "flex", alignItems: "center", gap: 10 }}>
                <Clock color="#0ECFD9" /> {isEN ? "Schedule Event" : "Termin planen"}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ background: "transparent", border: "none", color: T.textMuted, cursor: "pointer" }}><X size={20}/></button>
            </div>

            <div className="custom-scrollbar" style={{ flex: 1, overflowY: "auto", paddingRight: 8 }}>
              
              <Field label={isEN ? "TITLE" : "TITEL"} T={T}>
                <input value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} style={{ ...ctx.iS, width: "100%" }} placeholder="z.B. Weekly Sync" autoFocus />
              </Field>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Field label="START" T={T}>
                  <input type="datetime-local" value={eventForm.start_time} onChange={e => setEventForm({...eventForm, start_time: e.target.value})} style={{ ...ctx.iS, width: "100%" }} />
                </Field>
                <Field label="ENDE" T={T}>
                  <input type="datetime-local" value={eventForm.end_time} onChange={e => setEventForm({...eventForm, end_time: e.target.value})} style={{ ...ctx.iS, width: "100%" }} />
                </Field>
              </div>

              <Field label={isEN ? "EVENT TYPE" : "TERMIN-ART"} T={T}>
                <div style={{ display: "flex", gap: 10 }}>
                  {[
                    { id: 'meeting', label: 'Besprechung', icon: MessageSquare, color: '#3B82F6' },
                    { id: 'blocker', label: 'NL2 Blocker', icon: ShieldAlert, color: '#EF4444' },
                    { id: 'absence', label: 'Abwesenheit', icon: Coffee, color: '#F59E0B' }
                  ].map(type => {
                    const active = eventForm.type === type.id;
                    return (
                      <div key={type.id} onClick={() => setEventForm({...eventForm, type: type.id})} className="hov" style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "12px", background: active ? type.color+"20" : T.input, border: `1px solid ${active ? type.color : T.border2}`, borderRadius: 12, cursor: "pointer", color: active ? type.color : T.textMuted, transition: "all 0.2s" }}>
                        <type.icon size={20} />
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{type.label}</span>
                      </div>
                    )
                  })}
                </div>
              </Field>

              {/* 🚀 HIER IST DAS SMARTE DROPDOWN */}
              <Field label={isEN ? "RELATED TEAM (AUTO-INVITE)" : "ZUSTÄNDIGES TEAM (AUTO-INVITE)"} T={T}>
                <select value={eventForm.team_id} onChange={handleTeamSelect} style={{ ...ctx.iS, width: "100%", cursor: "pointer" }}>
                  <option value="">-- Kein spezifisches Team --</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <div style={{fontSize: 10, color: T.textGhost, marginTop: 6}}>Wählst du ein Team, werden alle Mitglieder automatisch unten eingeladen.</div>
              </Field>

              <Field label={isEN ? "PARTICIPANTS" : "TEILNEHMER EINLADEN"} T={T}>
                <div style={{ background: T.input, border: `1px solid ${T.border2}`, borderRadius: 12, padding: 12, maxHeight: 150, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                  {users.filter(u => u.id !== currentUser.id).map(u => {
                    const isInvited = eventForm.participants.includes(u.id);
                    return (
                      <div key={u.id} onClick={() => toggleParticipant(u.id)} className="hov" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", borderRadius: 8, background: isInvited ? "rgba(14,207,217,0.1)" : "transparent", cursor: "pointer" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <UserAvatar user={u} size={24} />
                          <span style={{ fontSize: 13, fontWeight: 600, color: isInvited ? "#0ECFD9" : T.text }}>{u.name}</span>
                        </div>
                        {isInvited && <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#0ECFD9", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={10} color="#000" /></div>}
                      </div>
                    )
                  })}
                </div>
              </Field>
              
              <Field label="NOTIZEN / AGENDA" T={T}>
                <textarea value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} style={{ ...ctx.iS, width: "100%", height: 80, resize: "vertical" }} placeholder="Worum geht es in dem Termin?" />
              </Field>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
              <button onClick={() => setShowModal(false)} className="hov" style={{ background: "transparent", border: `1px solid ${T.border2}`, color: T.textMuted, padding: "10px 16px", borderRadius: 10, cursor: "pointer", fontWeight: 600 }}>Abbrechen</button>
              <button onClick={saveEvent} disabled={isSubmitting} className="hov" style={{ background: "#0ECFD9", border: "none", color: "#000", padding: "10px 24px", borderRadius: 10, fontWeight: 800, cursor: "pointer", opacity: isSubmitting ? 0.5 : 1 }}>
                {isSubmitting ? "Wird geplant..." : "Termin eintragen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}